/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import Slider from "rc-slider";
import { SliderProps } from "rc-slider/lib/Slider";
import * as React from "react";

interface StyledSliderProps extends SliderProps {
  railBackground: string;
  trackBackgroundColor: string;
  handleSize: "s" | "l";
}

const StyledSlider = styled(Slider)<StyledSliderProps>`
  padding-bottom: 28px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &.rc-slider-disabled {
    background-color: unset;
  }

  .rc-slider-handle {
    background-color: var(--spectrum-white);
    margin-top: ${({ handleSize }): string =>
      handleSize === "l" ? "-9px" : "-4px"}; /* Adjust for larger handle size */
    height: ${({ handleSize }): string =>
      handleSize === "l" ? "42px" : "32px"};
    width: ${({ handleSize }): string =>
      handleSize === "l" ? "42px" : "32px"};
    border-width: 0px;
    opacity: 1;
    filter: drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.03))
      drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.15))
      drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.16));

    &:hover,
    &:active {
      box-shadow: 0px 0px 0px 2px rgba(var(--spectrum-indigo-800-rgb), 0.6);
    }

    &:focus {
      outline: solid 2px var(--spectrum-global-color-blue-500);
      outline-offset: 1px;
    }

    transform: translateX(
      ${(props: StyledSliderProps): string => {
        // hack to keep the handle within the bounds of the rail
        // Extra checks to make it less jittery.
        if (typeof props.value === "number") {
          if (props.value < 50) {
            return `${Math.max(16, props.value)}%`;
          } else if (props.value > 50) {
            return `${Math.min(85, props.value)}%`;
          }
        }
        return "50%";
      }}
    ) !important;
  }

  .rc-slider-rail {
    background: ${({ railBackground }): string => railBackground};
    height: 24px;
    border-radius: 16px;
  }

  .rc-slider-track {
    background-color: ${({ trackBackgroundColor }): string =>
      trackBackgroundColor};
    height: 24px;
    border-radius: 0 16px 16px 0;
  }
`;

interface StyledEnhanceSliderProps {
  disabled: boolean;
  onChange?: (value: number) => void;
  onChangeComplete?: (value: number) => void;
  value: number;
  ariaLabelledByForHandle?: string;
  ariaValueTextFormatterForHandle?: (value: number) => string;
  railBackground?: string;
  trackBackgroundColor?: string;
  handleSize?: "s" | "l";
}

export const StyledEnhanceSlider: React.FC<StyledEnhanceSliderProps> = ({
  disabled,
  onChange,
  onChangeComplete,
  value,
  ariaLabelledByForHandle,
  ariaValueTextFormatterForHandle,
  railBackground = "transparent linear-gradient(90deg, #ecda51 0%, #e55fcb 47%, #5158ec 100%) no-repeat padding-box",
  trackBackgroundColor = "var(--spectrum-indigo-400)",
  handleSize = "l",
}) => {
  return (
    <StyledSlider
      reverse
      max={100}
      step={1}
      disabled={disabled}
      onChange={(n: number): void => onChange?.(100 - n)}
      onChangeComplete={(n: number): void => onChangeComplete?.(100 - n)}
      value={100 - value}
      ariaLabelledByForHandle={ariaLabelledByForHandle}
      ariaValueTextFormatterForHandle={
        ariaValueTextFormatterForHandle
          ? (value): string => ariaValueTextFormatterForHandle(100 - value)
          : undefined
      }
      railBackground={railBackground}
      trackBackgroundColor={trackBackgroundColor}
      handleSize={handleSize}
    />
  );
};
